import { AutoStack } from 'components/AutoStack'
import { DownloadLink } from 'components/DownloadLink'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { FlexSpinner } from 'components/FlexSpinner'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { at } from 'lodash-es'
import {
  Blob,
  FileTemplateType,
  FileType,
  GeneratedDocument,
  ProvingUpdateType,
} from 'model'
import {
  DefaultButton,
  Dropdown,
  DropdownMenuItemType,
  Icon,
  IDropdownOption,
  PrimaryButton,
} from 'office-ui-fabric-react'
import * as path from 'path'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { isNullOrUndefined } from 'util'
import { useGenerateFileFromTemplateMutation } from 'views/AdminFileTemplatesView/AdminFileTemplatesView.generated'
import { useUpdateProvingMutation } from 'views/ProvingDetailsView/hooks'
import { useDeleteBlobMutation } from './documentGenerator.generated'

export interface DocumentGeneratorProps {
  templateType: FileTemplateType
  fieldPath: string
  getUpdate: (doc: GeneratedDocument) => ProvingUpdateType
  onDocCreated?: (file: Partial<Blob>) => void
}

export const DocumentGenerator: React.FC<DocumentGeneratorProps> = ({
  templateType,
  fieldPath,
  getUpdate,
  onDocCreated,
}) => {
  const [proving, refetch] = useCurrentProving()
  const { t } = useTranslation()
  const [generateDocument] = useGenerateFileFromTemplateMutation()
  const [updateProving] = useUpdateProvingMutation()
  const [deleteBlob] = useDeleteBlobMutation()
  const [isLoading, setIsLoading] = React.useState(false)
  const [currentDoc, setCurrentDoc] = React.useState(
    at(proving, fieldPath)[0] as unknown as GeneratedDocument | undefined
  )
  const [isRegenerateMode, setIsRegenerateMode] = React.useState(false)
  const [isReuploadMode, setIsReuploadMode] = React.useState(false)

  const templates =
    proving.provingGround && proving.provingGround.fileTemplates
      ? proving.provingGround.fileTemplates.filter(
          (ft) => ft.type === templateType
        )
      : []

  const [selectedTemplate, setSelectedTemplate] = React.useState(templates[0])

  const pgTemplates = templates.filter((templ) => !!templ.provingGround)
  const generalTemplates = templates.filter((templ) => !templ.provingGround)

  const pgTemplatesHeader =
    pgTemplates.length !== 0
      ? [
          {
            key: 'pgTemplatesHeader',
            text: proving.provingGround!.title,
            itemType: DropdownMenuItemType.Header,
          },
        ]
      : []

  const generalTemplatesHeader =
    generalTemplates.length !== 0
      ? [
          {
            key: 'generalTemplatesHeader',
            text: t('Alle Standorte'),
            itemType: DropdownMenuItemType.Header,
          },
        ]
      : []

  const templateOptions: IDropdownOption[] = isNullOrUndefined(selectedTemplate)
    ? [
        {
          key: '',
          text: t('Keine Vorlage verfügbar'),
        },
      ]
    : [
        ...pgTemplatesHeader,
        ...pgTemplates.map(
          (templ) =>
            ({ key: templ._id, text: templ.fileName } as IDropdownOption)
        ),
        { key: 'divider_1', text: '-', itemType: DropdownMenuItemType.Divider },
        ...generalTemplatesHeader,
        ...generalTemplates.map(
          (templ) =>
            ({ key: templ._id, text: templ.fileName } as IDropdownOption)
        ),
      ]

  const handleGenerateButtonClick = async (_: any, deleteOldDoc = false) => {
    if (!selectedTemplate) {
      return
    }
    setIsLoading(true)
    const generated = await generateDocument({
      variables: {
        isTestDoc: false,
        templateUri: selectedTemplate.uri,
        mapping: selectedTemplate.cellMapping || '',
        provingId: proving.id,
      },
    })

    if (!generated.data) {
      throw new Error('Document was not correctly generated!')
    }
    const blob = generated.data.generateFileFromTemplate

    if (onDocCreated) {
      onDocCreated(blob)
    }

    const generatedDoc = {
      name: `${t(`enum.fileTemplateType.${templateType}`)}_${proving.id}`,
      uri: blob.uri,
      type: getFileType(blob.name),
    }

    await updateProving({
      variables: {
        id: proving.id,
        update: getUpdate(generatedDoc),
      },
    })

    if (deleteOldDoc && currentDoc) {
      await deleteBlob({ variables: { uri: currentDoc.uri } })
    }

    setCurrentDoc(generatedDoc)
    await refetch()
    setIsLoading(false)
  }

  const handleRegenerateButtonClick = async () => {
    setIsLoading(true)
    await handleGenerateButtonClick(undefined, true)
    setIsRegenerateMode(false)
    setIsLoading(false)
  }

  const handleReuploadButtonClick = async (files: Partial<Blob>[]) => {
    setIsLoading(true)
    if (files[0]) {
      const uploadedBlob = files[0]
      if (onDocCreated) {
        onDocCreated(uploadedBlob)
      }
      if (uploadedBlob.uri && uploadedBlob.name && uploadedBlob.containerUri) {
        const generatedDoc = {
          name: `${t(`enum.fileTemplateType.${templateType}`)}_${proving.id}`,
          uri: uploadedBlob.uri,
          type: getFileType(uploadedBlob.name),
        }
        await updateProving({
          variables: {
            id: proving.id,
            update: getUpdate(generatedDoc),
          },
        })
        if (currentDoc) {
          await deleteBlob({ variables: { uri: currentDoc.uri } })
        }
        setCurrentDoc(generatedDoc)
      }
    }
    await refetch()
    setIsReuploadMode(false)
    setIsLoading(false)
  }

  const FileTemplateDropDown: React.FC = () => (
    <Dropdown
      disabled={isLoading || isNullOrUndefined(selectedTemplate)}
      style={{ width: 350 }}
      options={templateOptions}
      defaultSelectedKey={templates[0] ? templates[0]._id : ''}
      onChange={(_, item) => {
        if (item) {
          const template = templates.find((templ) => templ._id === item.key)
          if (template) {
            setSelectedTemplate(template)
          }
        }
      }}
    />
  )

  return (
    <>
      {/** an internal Memo has already been generated */}
      {currentDoc && (
        <AutoStack tokens={{ childrenGap: 12 }} horizontal={true}>
          <FlexSpinner loading={isLoading} style={{ width: 200 }}>
            <AutoStack style={{ width: 200 }} horizontalAlign={'center'}>
              <AutoStack horizontalAlign={'center'}>
                <Icon
                  iconName={getDocIconName(currentDoc)}
                  styles={{
                    root: { fontSize: 50, color: getDocIconColor(currentDoc) },
                  }}
                />
                <DownloadLink uri={currentDoc.uri}>
                  {currentDoc.name}
                </DownloadLink>
              </AutoStack>
            </AutoStack>
          </FlexSpinner>
          {!isRegenerateMode && !isReuploadMode && (
            <AutoStack tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text={t('Datei hochladen')}
                onClick={() => setIsReuploadMode(true)}
                disabled={isLoading}
              />
              <PrimaryButton
                text={`${t(
                  `enum.fileTemplateType.${templateType}`
                )} neu erstellen`}
                onClick={() => setIsRegenerateMode(true)}
                disabled={isLoading}
              />
            </AutoStack>
          )}
          {isRegenerateMode && (
            <AutoStack tokens={{ childrenGap: 8 }}>
              <FileTemplateDropDown />
              <AutoStack horizontal={true}>
                <PrimaryButton
                  text={`${t(
                    `enum.fileTemplateType.${templateType}`
                  )} erstellen`}
                  onClick={handleRegenerateButtonClick}
                  disabled={isLoading || isNullOrUndefined(selectedTemplate)}
                />
                <DefaultButton
                  text={t('action.cancel')}
                  onClick={() => setIsRegenerateMode(false)}
                  disabled={isLoading}
                />
              </AutoStack>
            </AutoStack>
          )}
          {isReuploadMode && (
            <AutoStack tokens={{ childrenGap: 8 }}>
              <FileUpload
                parentId={proving._id}
                parentType={'Proving'}
                singleFile={true}
                onUploadCompleted={handleReuploadButtonClick}
                text={t(`Datei per Drag 'n' Drop oder durch Klick hochladen`)}
              />
              <DefaultButton
                text={t('action.cancel')}
                onClick={() => setIsReuploadMode(false)}
                disabled={isLoading}
              />
            </AutoStack>
          )}
        </AutoStack>
      )}
      {/** an internal Memo has NOT been generated yet */}
      {!currentDoc && (
        <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
          <FileTemplateDropDown />
          <PrimaryButton
            text={`${t(`enum.fileTemplateType.${templateType}`)} erstellen`}
            onClick={handleGenerateButtonClick}
            disabled={isLoading || isNullOrUndefined(selectedTemplate)}
          />
        </AutoStack>
      )}
    </>
  )
}

function getFileType(fileName: string) {
  const ext = path.extname(fileName)
  switch (ext) {
    case '.docx': {
      return FileType.Word
    }
    case '.xlsx': {
      return FileType.Excel
    }
    default:
      return undefined
  }
}

function getDocIconName(doc: GeneratedDocument) {
  if (doc && doc.type) {
    switch (doc.type) {
      case FileType.Word:
        return 'WordDocument'
      case FileType.Excel:
        return 'ExcelDocument'
      default:
        return 'Document'
    }
  }
  return 'Document'
}

function getDocIconColor(doc: GeneratedDocument) {
  if (doc && doc.type) {
    switch (doc.type) {
      case FileType.Word:
        return '#315b9d'
      case FileType.Excel:
        return '#26764a'
      default:
        return '#737373'
    }
  }
  return '#737373'
}
